import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api.js';
import { useAuth } from '../../context/AuthContext.jsx';
import SignaturePad from '../../components/SignaturePad.jsx';
import { dateTime24 } from '../../utils/time24.js';
import CaptainPhoto from './CaptainPhoto.jsx';
import './sof-finalizer.scss';

const DELAY_REASONS = ['Port Congestion', 'Weather/Swell', 'Tug Machinery', 'Client Standby'];
const MILESTONE_LABELS = {
  dispatched: 'Dispatched',
  arrived: 'Arrived On-site',
  allfast: 'All Fast',
  commenced: 'Commenced',
  letgo: 'Let Go',
  returning: 'Returning',
  secured: 'Back at Station',
};

const CLOCK_LABELS = { start: 'Clock Started', stop: 'Clock Stopped' };
// The SoF can be signed once these are logged plus a clock start/stop (8790).
const REQUIRED_MILESTONES = ['arrived', 'allfast', 'letgo'];

/** Milestones and clock events as one ladder, in time order. */
function ladderRows(sof) {
  const rows = [
    ...(sof?.milestones || []).map((m) => ({
      key: `m-${m.id}`,
      kind: 'milestone',
      type: m.milestone_type,
      label: MILESTONE_LABELS[m.milestone_type] || m.milestone_type,
      at: m.logged_at,
    })),
    ...(sof?.clockEvents || []).map((c) => ({
      key: `c-${c.id}`,
      kind: 'clock',
      type: c.action,
      label: CLOCK_LABELS[c.action] || c.action,
      at: c.logged_at,
    })),
  ];
  return rows.sort((a, b) => new Date(a.at) - new Date(b.at));
}

/** What is still missing before the SoF can be submitted. */
function missingForSubmit(sof) {
  if (sof?.readiness) {
    const missing = sof.readiness.missingMilestones.map((t) => MILESTONE_LABELS[t] || t);
    if (!sof.readiness.hasClockPair) missing.push('a clock start and stop');
    return missing;
  }
  const logged = new Set((sof?.milestones || []).map((m) => m.milestone_type));
  const missing = REQUIRED_MILESTONES.filter((t) => !logged.has(t)).map((t) => MILESTONE_LABELS[t]);
  const events = sof?.clockEvents || [];
  const start = events.find((e) => e.action === 'start');
  const pair = start && events.some((e) => e.action === 'stop' && new Date(e.logged_at) >= new Date(start.logged_at));
  if (!pair) missing.push('a clock start and stop');
  return missing;
}

// 9031: 24-hour times (e.g. 29 Oct 2026, 14:05), never AM/PM.
const fmt = dateTime24;

export default function SofFinalizerPage() {
  const { user } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [sof, setSof] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [pageMsg, setPageMsg] = useState(null);

  const [delayForm, setDelayForm] = useState({
    reason: DELAY_REASONS[0],
    startTime: '',
    endTime: '',
    notes: '',
    evidenceFilename: '',
  });
  const [delayError, setDelayError] = useState('');
  const [signature, setSignature] = useState('');
  // 8928: the vessel master countersigns on this device.
  const [masterName, setMasterName] = useState('');
  const [masterSignature, setMasterSignature] = useState(null);
  // 9030: the captain's photo ({ photo, takenAt }) taken when finalising.
  const [captainPhoto, setCaptainPhoto] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const jobId = ticket?.id;

  const loadSof = useCallback(async (id) => {
    const s = await api.getSof(id);
    setSof(s);
  }, []);

  // 8955: open the captain's current job, or the one they pick (each job has its own SoF).
  const openJob = useCallback(
    async (id) => {
      setLoading(true);
      setLoadError('');
      setPageMsg(null);
      try {
        const t = await api.getActiveTicket(id);
        setTicket(t);
        setSof(null);
        setSignature('');
        setMasterName('');
        setMasterSignature(null);
        setCaptainPhoto(null);
        if (t) await loadSof(t.id);
      } catch (err) {
        setLoadError(err.message || 'Failed to load the Statement of Facts.');
      } finally {
        setLoading(false);
      }
    },
    [loadSof]
  );

  useEffect(() => {
    openJob(new URLSearchParams(window.location.search).get('jobId') || undefined);
  }, [openJob]);

  const addDelay = async (e) => {
    e.preventDefault();
    setDelayError('');
    if (!delayForm.startTime || !delayForm.endTime) {
      setDelayError('Provide the delay start and end times.');
      return;
    }
    try {
      await api.logDelay(jobId, {
        reason: delayForm.reason,
        startTime: new Date(delayForm.startTime).toISOString(),
        endTime: new Date(delayForm.endTime).toISOString(),
        notes: delayForm.notes.trim() || null,
        evidenceFilename: delayForm.evidenceFilename || null,
      });
      setDelayForm({ reason: DELAY_REASONS[0], startTime: '', endTime: '', notes: '', evidenceFilename: '' });
      await loadSof(jobId);
      setPageMsg({ type: 'success', text: 'Delay logged.' });
    } catch (err) {
      setDelayError(err.message || 'Failed to log delay.');
    }
  };

  const submitSof = async (e) => {
    e.preventDefault();
    setPageMsg(null);
    if (!signature.trim()) {
      setPageMsg({ type: 'danger', text: 'A captain e-signature is required to submit.' });
      return;
    }
    if (masterName.trim().length < 2) {
      setPageMsg({ type: 'danger', text: "Enter the vessel master's name." });
      return;
    }
    if (!masterSignature) {
      setPageMsg({ type: 'danger', text: 'Ask the vessel master to sign in the signature box.' });
      return;
    }
    if (!captainPhoto?.photo) {
      setPageMsg({ type: 'danger', text: 'Take a photo of yourself to finalise the Statement of Facts.' });
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.submitSof(jobId, {
        signature: signature.trim(),
        masterName: masterName.trim(),
        masterSignature,
        captainPhoto: captainPhoto.photo,
        captainPhotoTakenAt: captainPhoto.takenAt,
      });
      setPageMsg({
        type: 'success',
        text: `Statement of Facts submitted — job ${res.status}; rate computed at ${res.sof_charge ?? '—'}.`,
      });
      await loadSof(jobId);
    } catch (err) {
      setPageMsg({ type: 'danger', text: err.message || 'Failed to submit SoF.' });
    } finally {
      setSubmitting(false);
    }
  };

  const submitted = sof?.submitted;
  const ladder = ladderRows(sof);
  const missing = sof ? missingForSubmit(sof) : [];
  const sequenceErrors = sof?.readiness?.sequenceErrors || [];
  const blocked = missing.length > 0 || sequenceErrors.length > 0;

  return (
    <div className="container-fluid py-4" data-testid="sof-page" style={{ maxWidth: 960 }}>
      <div className="bg-primary text-white p-3 rounded shadow d-flex justify-content-between align-items-center mb-3">
        <div>
          <h6 className="mb-0 fw-bold">
            <i className="fas fa-file-signature me-2" aria-hidden="true" />
            Statement of Facts Finalizer
          </h6>
          <small className="opacity-75">
            {ticket?.booking_ref || '—'} · {ticket?.vessel_name || '—'}
            {ticket?.job_no ? ` · ${ticket.job_no}` : ''}
          </small>
        </div>
        <div className="d-flex align-items-center gap-2">
          {submitted && (
            <span className="badge bg-light text-success" data-testid="sof-status">
              <i className="fas fa-check me-1" aria-hidden="true" />
              {sof.status}
            </span>
          )}
          {/* 8929: print (or save as PDF) the Statement of Facts */}
          {ticket && sof && (
            <button type="button" className="btn btn-light btn-sm" onClick={() => window.print()} data-testid="print-sof">
              <i className="fas fa-print me-1" aria-hidden="true" />
              Print SoF
            </button>
          )}
        </div>
      </div>

      {loadError && (
        <div className="alert alert-danger" data-testid="sof-load-error">
          {loadError}
        </div>
      )}
      {pageMsg && (
        <div className={`alert alert-${pageMsg.type}`} role="alert" data-testid="sof-message">
          {pageMsg.text}
        </div>
      )}

      {loading ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
      ) : !ticket ? (
        <div className="alert alert-secondary">No active job to finalize.</div>
      ) : (
        <>
          {/* 8955: Booking → Service → Job → Dispatch Ticket, and the captain's other jobs */}
          <div className="card border-0 shadow-sm mb-3" data-testid="sof-job">
            <div className="card-body py-2 small">
              <div className="d-flex flex-wrap gap-3 align-items-center">
                <span>
                  <span className="text-muted">Booking</span> <strong>{ticket.booking_ref}</strong>
                </span>
                <i className="fas fa-angle-right text-muted" aria-hidden="true" />
                <span>
                  <span className="text-muted">Service</span>{' '}
                  <strong data-testid="sof-service">{ticket.service_name || ticket.movement_type}</strong>
                </span>
                {ticket.job_no && (
                  <>
                    <i className="fas fa-angle-right text-muted" aria-hidden="true" />
                    <span>
                      <span className="text-muted">Job</span>{' '}
                      <strong data-testid="sof-job-name">
                        {ticket.job_no} — {ticket.job_name}
                      </strong>
                      {ticket.booking_job_count > 1 && (
                        <span className="text-muted"> ({ticket.job_seq} of {ticket.booking_job_count})</span>
                      )}
                    </span>
                  </>
                )}
                <i className="fas fa-angle-right text-muted" aria-hidden="true" />
                <span>
                  <span className="text-muted">Ticket No.</span> <strong>{ticket.ticket_no ?? '—'}</strong>
                </span>
              </div>
              {(ticket.myJobs || []).length > 1 && (
                <div className="d-flex flex-wrap gap-2 align-items-center mt-2">
                  <label htmlFor="sof-my-job" className="fw-bold text-muted mb-0">
                    My jobs
                  </label>
                  <select
                    id="sof-my-job"
                    className="form-select form-select-sm w-auto flex-grow-1"
                    value={ticket.id}
                    onChange={(e) => openJob(e.target.value)}
                    data-testid="sof-job-select"
                  >
                    {ticket.myJobs.map((j) => (
                      <option key={j.id} value={j.id}>
                        {j.jobNo || `Ticket ${j.ticketNo}`} · {j.jobName} · {j.vesselName || '—'} · {fmt(j.scheduledAt)}
                        {j.status === 'Completed' ? ' · SoF signed' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Time ladder summary */}
          <div className="card border-0 shadow-sm mb-3">
            <div className="card-header bg-white pt-3 border-bottom-0">
              <h6 className="mb-0 text-secondary fw-bold small text-uppercase">
                <i className="fas fa-list-ol me-1" aria-hidden="true" />
                Time Ladder Summary
              </h6>
            </div>
            <div className="card-body">
              <ul className="list-group list-group-flush small" data-testid="sof-ladder">
                {ladder.length === 0 && (
                  <li className="list-group-item text-muted">No milestones logged.</li>
                )}
                {ladder.map((r) => (
                  <li
                    key={r.key}
                    className="list-group-item d-flex justify-content-between"
                    data-testid="sof-ladder-row"
                    data-kind={r.kind}
                    data-type={r.type}
                  >
                    <span>
                      {r.kind === 'clock' && <i className="fas fa-stopwatch me-1 text-muted" aria-hidden="true" />}
                      {r.label}
                    </span>
                    <span className="fw-bold">{fmt(r.at)}</span>
                  </li>
                ))}
              </ul>
              <p className="small text-muted mt-2 mb-0">Clock duration: {sof?.durationHours ?? '—'} h</p>
            </div>
          </div>

          {sof?.captainRemarks && (
            <div className="card border-0 shadow-sm mb-3" data-testid="sof-remarks">
              <div className="card-header bg-white pt-3 border-bottom-0">
                <h6 className="mb-0 text-secondary fw-bold small text-uppercase">
                  <i className="fas fa-pen-to-square me-1" aria-hidden="true" />
                  Captain&apos;s Remarks
                </h6>
              </div>
              <div className="card-body small" style={{ whiteSpace: 'pre-wrap' }}>
                {sof.captainRemarks}
              </div>
            </div>
          )}

          {/* Delay logger */}
          <div className="card border-0 shadow-sm mb-3">
            <div className="card-header bg-white pt-3 border-bottom-0">
              <h6 className="mb-0 text-secondary fw-bold small text-uppercase">
                <i className="fas fa-hourglass-half me-1 text-warning" aria-hidden="true" />
                Log Delay / Standby
              </h6>
            </div>
            <div className="card-body">
              {(sof?.delays || []).length > 0 && (
                <ul className="list-group list-group-flush small mb-3">
                  {sof.delays.map((d) => (
                    <li key={d.id} className="list-group-item" data-testid="delay-row" data-reason={d.reason}>
                      <div className="d-flex justify-content-between">
                        <span className="fw-bold">{d.reason}</span>
                        <span className="text-muted">
                          {fmt(d.start_time)} → {fmt(d.end_time)}
                        </span>
                      </div>
                      {d.evidence_filename && (
                        <span className="badge bg-light text-dark border mt-1">
                          <i className="fas fa-paperclip me-1" aria-hidden="true" />
                          {d.evidence_filename}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}

              {!submitted && (
                <form onSubmit={addDelay} noValidate>
                  {delayError && (
                    <div className="alert alert-danger py-2" role="alert" data-testid="delay-error">
                      {delayError}
                    </div>
                  )}
                  <div className="mb-2">
                    <label htmlFor="d-reason" className="form-label small fw-bold text-muted">
                      Delay Reason
                    </label>
                    <select
                      id="d-reason"
                      className="form-select form-select-sm"
                      value={delayForm.reason}
                      onChange={(e) => setDelayForm({ ...delayForm, reason: e.target.value })}
                      data-testid="delay-reason"
                    >
                      {DELAY_REASONS.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="row">
                    <div className="col-6 mb-2">
                      <label htmlFor="d-start" className="form-label small fw-bold text-muted">
                        Start
                      </label>
                      <input
                        id="d-start"
                        type="datetime-local"
                        step="1"
                        className="form-control form-control-sm"
                        value={delayForm.startTime}
                        onChange={(e) => setDelayForm({ ...delayForm, startTime: e.target.value })}
                        data-testid="delay-start"
                      />
                    </div>
                    <div className="col-6 mb-2">
                      <label htmlFor="d-end" className="form-label small fw-bold text-muted">
                        End
                      </label>
                      <input
                        id="d-end"
                        type="datetime-local"
                        step="1"
                        className="form-control form-control-sm"
                        value={delayForm.endTime}
                        onChange={(e) => setDelayForm({ ...delayForm, endTime: e.target.value })}
                        data-testid="delay-end"
                      />
                    </div>
                  </div>
                  <div className="mb-2">
                    <label htmlFor="d-notes" className="form-label small fw-bold text-muted">
                      Notes
                    </label>
                    <textarea
                      id="d-notes"
                      rows={2}
                      className="form-control form-control-sm"
                      value={delayForm.notes}
                      onChange={(e) => setDelayForm({ ...delayForm, notes: e.target.value })}
                      data-testid="delay-notes"
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="d-photo" className="form-label small fw-bold text-muted">
                      Evidence Photo
                    </label>
                    <input
                      id="d-photo"
                      type="file"
                      accept="image/*"
                      className="form-control form-control-sm"
                      onChange={(e) =>
                        setDelayForm({ ...delayForm, evidenceFilename: e.target.files?.[0]?.name || '' })
                      }
                      data-testid="delay-photo"
                    />
                    {delayForm.evidenceFilename && (
                      <span className="small text-muted">Attached: {delayForm.evidenceFilename}</span>
                    )}
                  </div>
                  <button className="btn btn-outline-primary btn-sm w-100" data-testid="save-delay">
                    <i className="fas fa-plus me-1" aria-hidden="true" />
                    Add Delay Log
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* SoF signoff */}
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white pt-3 border-bottom-0">
              <h6 className="mb-0 text-secondary fw-bold small text-uppercase">
                <i className="fas fa-signature me-1 text-primary" aria-hidden="true" />
                Statement of Facts Signoff
              </h6>
            </div>
            <div className="card-body">
              {submitted ? (
                <>
                  <div className="alert alert-success mb-2" data-testid="sof-charge">
                    Submitted by <strong>{sof.signedBy}</strong>. Job {sof.status}; computed charge{' '}
                    <strong>{sof.charge ?? '—'}</strong>.
                  </div>
                  {sof.masterName && (
                    <div className="small" data-testid="sof-master-signed">
                      <span className="text-muted">Vessel master: </span>
                      <strong>{sof.masterName}</strong>
                      {sof.masterSignature && (
                        <img src={sof.masterSignature} alt={`Signature of ${sof.masterName}`} className="sof-signature-img d-block mt-1" />
                      )}
                    </div>
                  )}
                  {/* 9030 */}
                  {sof.captainPhoto && (
                    <div className="small mt-2" data-testid="sof-captain-photo">
                      <span className="text-muted">Captain&apos;s photo: </span>
                      <span data-testid="sof-captain-photo-at">{fmt(sof.captainPhotoAt)}</span>
                      <img src={sof.captainPhoto} alt={`Photo of ${sof.signedBy}`} className="sof-photo-img d-block mt-1" />
                    </div>
                  )}
                </>
              ) : (
                <form onSubmit={submitSof} noValidate>
                  <div className="mb-3">
                    <label htmlFor="sof-sign" className="form-label small fw-bold text-muted">
                      Captain E-signature <span className="text-danger">*</span>
                    </label>
                    <input
                      id="sof-sign"
                      className="form-control form-control-sm"
                      value={signature}
                      onChange={(e) => setSignature(e.target.value)}
                      placeholder="Type your full name to sign"
                      data-testid="sof-signature"
                      aria-describedby="sof-sign-help"
                    />
                    <div id="sof-sign-help" className="form-text">
                      Sign as the captain on this job, exactly as registered
                      {user?.name ? <>: <strong>{user.name}</strong></> : ''}.
                    </div>
                  </div>
                  {/* 8928: the vessel master countersigns the SoF */}
                  <div className="mb-2">
                    <label htmlFor="sof-master-name" className="form-label small fw-bold text-muted">
                      Vessel Master&apos;s Name <span className="text-danger">*</span>
                    </label>
                    <input
                      id="sof-master-name"
                      className="form-control form-control-sm"
                      value={masterName}
                      maxLength={160}
                      onChange={(e) => setMasterName(e.target.value)}
                      placeholder="Master of the assisted vessel"
                      data-testid="sof-master-name"
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="sof-master-signature" className="form-label small fw-bold text-muted">
                      Vessel Master&apos;s Signature <span className="text-danger">*</span>
                    </label>
                    <SignaturePad id="sof-master-signature" onChange={setMasterSignature} testId="sof-master-signature" />
                  </div>
                  {/* 9030: the captain takes a photo of themself to finalise */}
                  <div className="mb-3">
                    <div className="form-label small fw-bold text-muted">
                      Captain&apos;s Photo <span className="text-danger">*</span>
                    </div>
                    <CaptainPhoto value={captainPhoto} onChange={setCaptainPhoto} />
                  </div>
                  {missing.length > 0 && (
                    <div className="alert alert-warning py-2 small" data-testid="sof-missing">
                      Log these before submitting: {missing.join(', ')}.
                    </div>
                  )}
                  {sequenceErrors.length > 0 && (
                    <div className="alert alert-danger py-2 small" data-testid="sof-sequence-errors">
                      The time ladder is out of sequence — have the office correct it before signing:
                      <ul className="mb-0 mt-1">
                        {sequenceErrors.map((e) => (
                          <li key={e}>{e}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <button
                    className="btn btn-primary w-100"
                    disabled={submitting || blocked}
                    data-testid="submit-sof"
                  >
                    {submitting && (
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    )}
                    <i className="fas fa-check-double me-1" aria-hidden="true" />
                    Submit Completed SoF
                  </button>
                </form>
              )}
            </div>
          </div>
        </>
      )}

      {/* 8929: what Print SoF puts on paper */}
      {ticket && sof && (
        <div className="sof-print" data-testid="sof-print">
          <h2>Statement of Facts</h2>
          {!submitted && <p className="sof-print-draft">DRAFT: not yet signed</p>}
          <table className="sof-print-facts">
            <tbody>
              <tr>
                <th>Booking</th>
                <td>{ticket.booking_ref || '—'}</td>
                <th>Ticket No.</th>
                <td>{ticket.ticket_no ?? '—'}</td>
              </tr>
              <tr>
                <th>Vessel</th>
                <td>{ticket.vessel_name || '—'}</td>
                <th>Tug</th>
                <td>{ticket.tug_name || '—'}</td>
              </tr>
              <tr>
                <th>Service</th>
                <td>{ticket.service_name || ticket.movement_type || '—'}</td>
                <th>Port / Berth</th>
                <td>
                  {[ticket.port_name, ticket.berth_name].filter(Boolean).join(' / ') || '—'}
                  {ticket.assistance_area_name ? ` (${ticket.assistance_area_name})` : ''}
                </td>
              </tr>
              {ticket.job_no && (
                <tr>
                  <th>Job</th>
                  <td colSpan={3}>
                    {ticket.job_no} — {ticket.job_name}
                    {ticket.booking_job_count > 1 ? ` (job ${ticket.job_seq} of ${ticket.booking_job_count})` : ''}
                  </td>
                </tr>
              )}
              <tr>
                <th>Scheduled</th>
                <td>{fmt(ticket.scheduled_at)}</td>
                <th>Clock duration</th>
                <td>{sof.durationHours ?? '—'} h</td>
              </tr>
            </tbody>
          </table>

          <h3>Time Ladder</h3>
          <table className="sof-print-table">
            <tbody>
              {ladder.length === 0 && (
                <tr>
                  <td colSpan={2}>No milestones logged.</td>
                </tr>
              )}
              {ladder.map((r) => (
                <tr key={r.key}>
                  <td>{r.label}</td>
                  <td>{fmt(r.at)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {(sof.delays || []).length > 0 && (
            <>
              <h3>Delays / Standby</h3>
              <table className="sof-print-table">
                <tbody>
                  {sof.delays.map((d) => (
                    <tr key={d.id}>
                      <td>{d.reason}</td>
                      <td>
                        {fmt(d.start_time)} → {fmt(d.end_time)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {sof.captainRemarks && (
            <>
              <h3>Captain&apos;s Remarks</h3>
              <p className="sof-print-remarks">{sof.captainRemarks}</p>
            </>
          )}

          <div className="sof-print-signatures">
            <div>
              <div className="sof-print-sign-box">{sof.signedBy || ''}</div>
              <div>Tug Captain{sof.signedBy ? ` — e-signed ${fmt(sof.signedAt)}` : ''}</div>
            </div>
            <div>
              <div className="sof-print-sign-box">
                {sof.masterSignature && <img src={sof.masterSignature} alt={`Signature of ${sof.masterName}`} />}
              </div>
              <div>Vessel Master{sof.masterName ? `: ${sof.masterName}` : ''}</div>
            </div>
          </div>
          {/* 9030 */}
          {sof.captainPhoto && (
            <div className="sof-print-photo">
              <img src={sof.captainPhoto} alt={`Photo of ${sof.signedBy}`} />
              <div>Captain&apos;s photo, taken {fmt(sof.captainPhotoAt)}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
